import React, { useState } from 'react';
import {
  XMarkIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  LightBulbIcon,
  CalendarIcon,
  FireIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const AIAnalysisModal = ({ isOpen, onClose, exam, onAnalyze }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    setFromCache(false);
    try {
      const result = await onAnalyze();
      setAnalysis(result.analysis || result);
      setFromCache(result.fromCache || false);
    } catch (err) {
      setError(err.message || 'Analiz yapılırken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return '🔴';
      case 'medium':
        return '🟡';
      case 'low':
        return '🟢';
      default:
        return '⚪';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <SparklesIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    AI Performans Analizi
                  </h2>
                  <p className="text-purple-100 text-sm mt-0.5">
                    Yapay zeka destekli kişiselleştirilmiş öneriler
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-6">
            {/* Initial State - No Analysis Yet */}
            {!analysis && !loading && !error && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full mb-6">
                  <SparklesIcon className="w-10 h-10 text-purple-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Performansınızı AI ile Analiz Edin
                </h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Yapay zeka, deneme sonuçlarınızı analiz ederek size özel öneriler sunacak.
                  Hangi derslere odaklanmalısınız, neler yapabilirsiniz öğrenin.
                </p>
                <button
                  onClick={handleAnalyze}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-indigo-700 transition shadow-lg hover:shadow-xl"
                >
                  <SparklesIcon className="w-5 h-5" />
                  Analizi Başlat
                </button>
                <p className="text-sm text-gray-500 mt-4">
                  💡 Analiz yaklaşık 5-10 saniye sürecek
                </p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-full mb-6 animate-pulse">
                  <SparklesIcon className="w-10 h-10 text-purple-600 animate-spin" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  AI Analiz Ediyor...
                </h3>
                <p className="text-gray-600 mb-4">
                  Deneme sonuçlarınız yapay zeka tarafından değerlendiriliyor
                </p>
                <div className="flex items-center justify-center gap-2">
                  <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-purple-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-6">
                  <XMarkIcon className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Bir Hata Oluştu
                </h3>
                <p className="text-gray-600 mb-6">
                  {error}
                </p>
                <button
                  onClick={handleAnalyze}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 transition"
                >
                  <ArrowPathIcon className="w-5 h-5" />
                  Tekrar Dene
                </button>
              </div>
            )}

            {/* Analysis Results */}
            {analysis && (
              <div className="space-y-6">
                {/* Cache Badge */}
                {fromCache && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <FireIcon className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">
                      Bu analiz önbellekten getirildi (Anında sonuç!)
                    </span>
                  </div>
                )}

                {/* Summary */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-500 p-3 rounded-lg">
                      <SparklesIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        Genel Değerlendirme
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {analysis.summary}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Improvements & Declines */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Improvements */}
                  {analysis.improvements && analysis.improvements.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <ArrowTrendingUpIcon className="w-5 h-5 text-green-600" />
                        <h4 className="font-bold text-green-900">İlerleme Gösteren</h4>
                      </div>
                      <ul className="space-y-2">
                        {analysis.improvements.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-green-800">
                            <span className="text-green-500 mt-0.5">✓</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Declines */}
                  {analysis.declines && analysis.declines.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <ArrowTrendingDownIcon className="w-5 h-5 text-red-600" />
                        <h4 className="font-bold text-red-900">Gerileme Gösteren</h4>
                      </div>
                      <ul className="space-y-2">
                        {analysis.declines.map((item, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-red-800">
                            <span className="text-red-500 mt-0.5">⚠</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Recommendations */}
                {analysis.recommendations && analysis.recommendations.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-5">
                      <LightBulbIcon className="w-6 h-6 text-yellow-500" />
                      <h4 className="font-bold text-gray-900 text-lg">Kişiselleştirilmiş Öneriler</h4>
                    </div>
                    <div className="space-y-4">
                      {analysis.recommendations.map((rec, index) => (
                        <div
                          key={index}
                          className={`border rounded-lg p-4 ${getPriorityColor(rec.priority)}`}
                        >
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">{getPriorityIcon(rec.priority)}</span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <h5 className="font-bold">{rec.subject}</h5>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-white/50">
                                  {rec.priority === 'high' ? 'Yüksek Öncelik' :
                                   rec.priority === 'medium' ? 'Orta Öncelik' : 'Düşük Öncelik'}
                                </span>
                              </div>
                              <p className="text-sm leading-relaxed">
                                {rec.advice}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Study Plan */}
                {analysis.studyPlan && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-purple-500 p-3 rounded-lg">
                        <CalendarIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Bu Hafta Çalışma Planı</h4>
                        <p className="text-gray-700 leading-relaxed">
                          {analysis.studyPlan}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Target Progress */}
                {analysis.targetProgress && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                    <div className="flex items-start gap-4">
                      <div className="bg-indigo-500 p-3 rounded-lg">
                        <FireIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-2">Hedefinize Ne Kadar Yakınsınız?</h4>
                        <p className="text-gray-700 leading-relaxed">
                          {analysis.targetProgress}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Motivation */}
                {analysis.motivation && (
                  <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-xl p-6 text-white">
                    <div className="flex items-start gap-4">
                      <span className="text-4xl">💪</span>
                      <div className="flex-1">
                        <h4 className="font-bold text-xl mb-2">Motivasyon</h4>
                        <p className="text-pink-50 leading-relaxed text-lg">
                          {analysis.motivation}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Re-analyze Button */}
                <div className="flex justify-center pt-4">
                  <button
                    onClick={handleAnalyze}
                    className="inline-flex items-center gap-2 bg-gray-100 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-200 transition"
                  >
                    <ArrowPathIcon className="w-5 h-5" />
                    Yeniden Analiz Et
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAnalysisModal;
